import { useState } from "react";
import { Package, Trash2, AlertCircle, Check, Merge } from "lucide-react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { CSICodePicker } from "@/components/csi-code-picker";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import {
  LocationDropdown,
  SubmittalStatusDropdown,
  SubmittalTypeDropdown,
  ReviewTimeDropdown,
  InternalReviewDropdown,
} from "@/components/submittal/column-dropdowns";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { TrackerType } from "@/components/tracker-view-modal";
import { CustomTrackerDialog } from "./custom-tracker-dialog";
import { TrackerMenuItems } from "./tracker-menu-items";
import { TrackerSummaryButton } from "./tracker-summary-button";
import type { CustomTrackerInfo, TrackerCounts } from "./tracker-config";

interface OrgMemberOption {
  _id: string;
  name: string;
}

interface NamedCustomItem {
  _id: string;
  name: string;
}

interface SubmittalConfigCardProps {
  orgMembers: OrgMemberOption[];
  selectedRowCount: number;
  submittalManager: string | null;
  designReviewTime: string;
  internalReviewTime: string;
  trackerCounts: TrackerCounts;
  customTrackerNames: CustomTrackerInfo[];
  csiCodeCol: number;
  locationCol?: number;
  locations?: NamedCustomItem[];
  projectId?: Id<"projects">;
  submittalStatusCol?: number;
  submittalTypeCol?: number;
  reviewTimesConfirmed: boolean;
  csiCodesVerified: boolean;
  onSubmittalManagerChange: (managerName: string) => void;
  onDesignReviewTimeChange: (value: string) => void;
  onInternalReviewTimeChange: (value: string) => void;
  onConfirmReviewTimes: () => void;
  onVerifyCSICodes: () => void;
  onTrackerClick: (type: TrackerType, customTrackerName?: string) => void;
  onBulkCSIAssign: (code: string, title: string, divisionCode?: string, divisionTitle?: string) => void;
  onBulkColumnAssign: (colIndex: number, value: string, columnName: string) => void;
  createCustomItem?: (args: {
    projectId: Id<"projects">;
    type: "location";
    name: string;
  }) => Promise<unknown>;
  onAddToTracker: (type: TrackerType, customTrackerName?: string) => void;
  onCreateCustomTracker: (name: string) => void;
  onDeleteRows: () => void;
  onMergeRows: () => void;
  onClearSelection: () => void;
  className?: string;
}

export function SubmittalConfigCard({
  orgMembers,
  selectedRowCount,
  submittalManager,
  designReviewTime,
  internalReviewTime,
  trackerCounts,
  customTrackerNames,
  csiCodeCol,
  locationCol,
  locations = [],
  projectId,
  submittalStatusCol,
  submittalTypeCol,
  reviewTimesConfirmed,
  csiCodesVerified,
  onSubmittalManagerChange,
  onDesignReviewTimeChange,
  onInternalReviewTimeChange,
  onConfirmReviewTimes,
  onVerifyCSICodes,
  onTrackerClick,
  onBulkCSIAssign,
  onBulkColumnAssign,
  createCustomItem,
  onAddToTracker,
  onCreateCustomTracker,
  onDeleteRows,
  onMergeRows,
  onClearSelection,
  className,
}: SubmittalConfigCardProps) {
  const hasSelection = selectedRowCount > 0;
  const managerNotSet = submittalManager === null;
  const [isCreateTrackerDialogOpen, setIsCreateTrackerDialogOpen] = useState(false);

  return (
    <div
      className={cn(
        "border rounded-sm overflow-hidden",
        className
      )}
    >
      {/* Top row: Configuration, Review Times, Bulk Actions */}
      <div className="flex">
        {/* Configuration Section */}
        <div className="flex-1 border-r p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Configuration
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Manager:</span>
              <Select
                value={submittalManager ?? undefined}
                onValueChange={onSubmittalManagerChange}
              >
                <SelectTrigger 
                  className={cn(
                    "h-8 text-sm w-[160px]",
                    managerNotSet && "border-yellow-500/50 shadow-[0_0_8px_rgba(234,179,8,0.3)] bg-yellow-500/5"
                  )}
                >
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  {orgMembers.map((member) => (
                    <SelectItem key={member._id} value={member.name}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasSelection ? (
              <p className="text-xs text-primary font-medium">
                Will apply to {selectedRowCount} selected row{selectedRowCount !== 1 ? "s" : ""}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                No rows selected - will apply to all rows
              </p>
            )}
          </div>
        </div>

        {/* Review Times Section */}
        <div className="flex-1 border-r p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Review Times
            </h3>
            {reviewTimesConfirmed ? (
              <div className="flex items-center gap-1 text-green-600">
                <Check className="size-3" />
                <span className="text-xs">Confirmed</span>
              </div>
            ) : (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs px-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                onClick={onConfirmReviewTimes}
              >
                Confirm
              </Button>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Design:</span>
              <ReviewTimeDropdown
                value={designReviewTime}
                onChange={onDesignReviewTimeChange}
                className={cn(
                  "h-8 text-sm w-[100px]",
                  !reviewTimesConfirmed && "border-yellow-500/30"
                )}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Internal:</span>
              <InternalReviewDropdown
                value={internalReviewTime}
                onChange={onInternalReviewTimeChange}
                className={cn(
                  "h-8 text-sm w-[100px]",
                  !reviewTimesConfirmed && "border-yellow-500/30"
                )}
              />
            </div>
          </div>
        </div>

        {/* Bulk Actions Section */}
        <div className="flex-[1.5] border-r p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Bulk Actions
            </h3>
            {csiCodeCol >= 0 && (
              csiCodesVerified ? (
                <div className="flex items-center gap-1 text-green-600">
                  <Check className="size-3" />
                  <span className="text-xs">CSI Verified</span>
                </div>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 text-xs px-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                  onClick={onVerifyCSICodes}
                >
                  Verify CSI
                </Button>
              )
            )}
            {!hasSelection && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertCircle className="size-3" />
                Select rows to use
              </span>
            )}
            {hasSelection && (
              <span className="text-xs font-medium text-primary">
                {selectedRowCount} row{selectedRowCount !== 1 ? "s" : ""} selected
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {csiCodeCol >= 0 && (
              <div className={cn("relative", !hasSelection && "opacity-50")}>
                <CSICodePicker
                  onSelect={(code, title, divisionCode, divisionTitle) => onBulkCSIAssign(code, title, divisionCode, divisionTitle)}
                  className="h-8 text-sm"
                  disabled={!hasSelection}
                />
              </div>
            )}
            {submittalStatusCol !== undefined && submittalStatusCol >= 0 && (
              <div className={cn(!hasSelection && "opacity-50")}>
                <SubmittalStatusDropdown
                  placeholder="Set Status"
                  onChange={(value) => onBulkColumnAssign(submittalStatusCol, value, "Status")}
                  className="h-8 text-sm w-[140px]"
                  disabled={!hasSelection}
                />
              </div>
            )}
            {submittalTypeCol !== undefined && submittalTypeCol >= 0 && (
              <div className={cn(!hasSelection && "opacity-50")}>
                <SubmittalTypeDropdown
                  placeholder="Set Type"
                  onChange={(value) => onBulkColumnAssign(submittalTypeCol, value, "Type")}
                  className="h-8 text-sm w-[140px]"
                  disabled={!hasSelection}
                />
              </div>
            )}
            {locationCol !== undefined && locationCol >= 0 && projectId && createCustomItem && (
              <div className={cn(!hasSelection && "opacity-50")}>
                <LocationDropdown
                  locations={locations}
                  onChange={(value) => onBulkColumnAssign(locationCol, value, "Location")}
                  onAddNew={async (name) => {
                    await createCustomItem({
                      projectId,
                      type: "location",
                      name,
                    });
                  }}
                  placeholder="Set Location"
                  className="h-8 text-sm w-[150px] max-w-[150px]"
                  disabled={!hasSelection}
                />
              </div>
            )}
            <div className={cn(!hasSelection && "opacity-50")}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-sm" disabled={!hasSelection}>
                    <Package className="size-3.5 mr-1" />
                    Add to Tracker
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <TrackerMenuItems
                    trackerCounts={trackerCounts}
                    customTrackers={customTrackerNames}
                    onAddToTracker={onAddToTracker}
                    onCreateCustomTracker={() => setIsCreateTrackerDialogOpen(true)}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className={cn(!hasSelection && "opacity-50")}>
              <DeleteConfirmationDialog
                title="Delete selected rows?"
                description={`This will delete ${selectedRowCount} selected row${selectedRowCount !== 1 ? "s" : ""} from the submittal.`}
                onConfirm={onDeleteRows}
                confirmLabel="Delete"
              >
                <Button variant="destructive" size="sm" className="h-8 text-sm" disabled={!hasSelection}>
                  <Trash2 className="size-3.5 mr-1" />
                  Delete
                </Button>
              </DeleteConfirmationDialog>
            </div>
            {selectedRowCount >= 2 && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-sm"
                  onClick={onMergeRows}
                >
                  <Merge className="size-3.5 mr-1" />
                  Merge
                </Button>
              </div>
            )}
            {hasSelection && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-sm"
                onClick={onClearSelection}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Trackers Section */}
        <div className="p-3 min-w-[200px]">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Trackers
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <TrackerSummaryButton
              label="Warranty"
              count={trackerCounts.warranty}
              onClick={() => onTrackerClick("warranty")}
              compact
            />
            <TrackerSummaryButton
              label="Lead"
              count={trackerCounts.lead_time}
              onClick={() => onTrackerClick("lead_time")}
              compact
            />
            <TrackerSummaryButton
              label="Attic"
              count={trackerCounts.attic_stock}
              onClick={() => onTrackerClick("attic_stock")}
              compact
            />
            {customTrackerNames.map((tracker) => (
              <TrackerSummaryButton
                key={tracker.name}
                label={tracker.name}
                count={tracker.count}
                onClick={() => onTrackerClick("custom", tracker.name)}
                compact
              />
            ))}
          </div>
        </div>
      </div>

      <CustomTrackerDialog
        open={isCreateTrackerDialogOpen}
        onOpenChange={setIsCreateTrackerDialogOpen}
        onCreateTracker={onCreateCustomTracker}
      />
    </div>
  );
}
